import {
  CircularProgress,
  Dialog,
  DialogContent,
  Divider,
  FormControl,
  FormHelperText,
  Grid,
  MenuItem,
  Stack,
  Typography,
  Autocomplete,
} from "@mui/material";
import React, { useState } from "react";
import UserAvatar from "../../common/UserAvatar";
import { useFormik } from "formik";
import * as yup from "yup";
import {
  StyledSelect,
  StyledTextField,
} from "../../customComponents/styled/styledInputs";
import CustomFileInput from "../../customComponents/CustomFileInput";
import { StyledLoadingButton } from "../../customComponents/styled/styledButtons";
import { Box } from "@mui/system";
import CloseSharpIcon from "@mui/icons-material/CloseSharp";
import { StyledPaper } from "../../customComponents/styled/styledPaper";
import {
  useAddStudentToClassroomMutation,
  useUpdateStudentMutation,
} from "./studentApiSlice";
import { useUploadFileMutation } from "../../../app/utilsApiSlice";
import { useGetClassesPaginatedQuery } from "../classroom/classApiSlice";
import toast from "../../../helpers/toast";
import { format, parseISO } from "date-fns";

const validationSchema = yup.object({
  first_name: yup.string().required("Required"),
  last_name: yup.string().required("Required"),
  email: yup.string().email("Enter a valid email"),
  gender: yup.string().required("Required"),
  classroom: yup.string().required("Required"),
  dob: yup.string(),
  address: yup.string(),
  phone: yup.string(),
});

function EditStudentDialog({
  open,
  setOpen,
  selectedStudent,
  currentClassroom,
}) {
  const [classroomSearch, setClassroomSearch] = useState("");
  const { data: classroomsData } = useGetClassesPaginatedQuery({ page: 1, per_page: 10, q: classroomSearch });
  const classrooms = classroomsData?.classrooms ?? [];
  const [uploadFile, { isLoading: fileLoading }] = useUploadFileMutation();
  const [updateStudent, { isLoading: updateStudentLoading }] =
    useUpdateStudentMutation();
  const [addStudentToClass] = useAddStudentToClassroomMutation();

  const formik = useFormik({
    initialValues: {
      id: selectedStudent?.id,
      first_name: selectedStudent?.first_name || "",
      last_name: selectedStudent?.last_name || "",
      email: selectedStudent?.email || "",
      dob: selectedStudent?.dob
        ? format(parseISO(selectedStudent?.dob), "yyyy-MM-dd")
        : "",
      gender: selectedStudent?.gender || "",
      classroom: (currentClassroom && currentClassroom) || "",
      address: selectedStudent?.address || "",
      phone: selectedStudent?.phone || "",
      photo: selectedStudent?.photo || "",
      parent_email: selectedStudent?.parent_email || "",
      parent_email2: selectedStudent?.parent_email2 || "",
      height: selectedStudent?.height || "",
      weight: selectedStudent?.weight || "",
    },
    enableReinitialize: true,
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      try {
        await updateStudent(values).unwrap();
        toast.success("Student updated successfully");
        if (values.classroom !== currentClassroom) {
          await addStudentToClass({
            id: values.id,
            classroom_id: values.classroom,
          }).unwrap();
          toast.success("Student classroom changed");
        }
      } catch (error) {}
      handleClose();
    },
  });

  const handleClose = () => {
    setOpen(false);
    formik.resetForm();
  };

  const handleFileChange = async (e) => {
    try {
      const file = e.target.files[0];
      const formData = new FormData();
      formData.append("file", file);
      const response = await uploadFile(formData).unwrap();
      if (response) {
        formik.setFieldValue(e.target.name, response.url);
      }
    } catch (error) {}
  };

  return (
    <Dialog
      onClose={handleClose}
      open={open}
      fullWidth
      maxWidth="xl"
      PaperProps={{
        sx: {
          width: "min(1200px, 96vw)",
          maxWidth: "1200px",
        },
      }}
    >
      <Box sx={{ display: "flex", padding: 2, justifyContent: "flex-end" }}>
        <CloseSharpIcon onClick={handleClose} sx={{ cursor: "pointer" }} />
      </Box>
      <Divider />

      <DialogContent>
        <Grid container columnSpacing={3}>
          <Grid item xs={12} lg={8}>
            <StyledPaper>
              <Box className="paperHeader">Update Profile</Box>
              <Box className="paperBody">
                <Box component="form" onSubmit={formik.handleSubmit}>
                  <Grid container columnSpacing={1} rowSpacing={3}>
                    <Grid item xs={12} lg={6}>
                      <FormControl
                        fullWidth
                        error={
                          formik.touched.first_name && formik.errors.first_name
                        }
                      >
                        <Typography variant="formSubHeading">
                          first name
                        </Typography>
                        <StyledTextField
                          bold
                          variant="outlined"
                          id="first_name"
                          name="first_name"
                          value={formik.values.first_name}
                          onChange={formik.handleChange}
                          error={
                            formik.touched.first_name &&
                            Boolean(formik.errors.first_name)
                          }
                        />
                        <FormHelperText variant="error">
                          {formik.touched.first_name &&
                            formik.errors.first_name}
                        </FormHelperText>
                      </FormControl>
                    </Grid>

                    <Grid item xs={12} lg={6}>
                      <FormControl
                        fullWidth
                        error={
                          formik.touched.last_name &&
                          Boolean(formik.errors.last_name)
                        }
                      >
                        <Typography variant="formSubHeading">
                          last name
                        </Typography>
                        <StyledTextField
                          bold
                          variant="outlined"
                          id="last_name"
                          name="last_name"
                          value={formik.values.last_name}
                          onChange={formik.handleChange}
                          error={
                            formik.touched.last_name &&
                            Boolean(formik.errors.last_name)
                          }
                        />
                        <FormHelperText variant="error">
                          {formik.touched.last_name && formik.errors.last_name}
                        </FormHelperText>
                      </FormControl>
                    </Grid>
                    {/* end of first row */}

                    {/* beggining second row inputs */}

                    <Grid item xs={12} md={4}>
                      <FormControl
                        error={formik.touched.dob && formik.errors.dob}
                        fullWidth
                      >
                        <Typography htmlFor="section" variant="formSubHeading">
                          Date of birth
                        </Typography>
                        <StyledTextField
                          type="date"
                          id="dob"
                          name="dob"
                          value={formik.values.dob}
                          onChange={formik.handleChange}
                          error={
                            formik.touched.dob && Boolean(formik.errors.dob)
                          }
                        />

                        <FormHelperText variant="error">
                          {formik.touched.dob && formik.errors.dob}
                        </FormHelperText>
                      </FormControl>
                    </Grid>

                    <Grid item xs={12} md={4}>
                      <FormControl
                        error={formik.touched.gender && formik.errors.gender}
                        fullWidth
                      >
                        <Typography htmlFor="gender" variant="formSubHeading">
                          gender
                        </Typography>
                        <StyledSelect
                          displayEmpty
                          id="gender"
                          name="gender"
                          value={formik.values.gender}
                          onChange={formik.handleChange}
                          error={
                            formik.touched.gender &&
                            Boolean(formik.errors.gender)
                          }
                        >
                          <MenuItem value="" disabled>
                            <em>select gender</em>
                          </MenuItem>
                          <MenuItem value="Male">Male</MenuItem>
                          <MenuItem value="Female">Female</MenuItem>
                        </StyledSelect>
                        <FormHelperText variant="error">
                          {formik.touched.gender && formik.errors.gender}
                        </FormHelperText>
                      </FormControl>
                    </Grid>

                    <Grid item xs={12} md={2}>
                      <FormControl fullWidth>
                        <Typography variant="formSubHeading">
                          height (cm)
                        </Typography>
                        <StyledTextField
                          type="number"
                          id="height"
                          name="height"
                          placeholder="e.g. 145"
                          value={formik.values.height}
                          onChange={formik.handleChange}
                        />
                      </FormControl>
                    </Grid>

                    <Grid item xs={12} md={2}>
                      <FormControl fullWidth>
                        <Typography variant="formSubHeading">
                          weight (kg)
                        </Typography>
                        <StyledTextField
                          type="number"
                          id="weight"
                          name="weight"
                          placeholder="e.g. 40"
                          value={formik.values.weight}
                          onChange={formik.handleChange}
                        />
                      </FormControl>
                    </Grid>

                    <Grid item xs={12} sm={6} md={4}>
                      <FormControl fullWidth>
                        <Typography variant="formSubHeading">photo</Typography>

                        <Stack direction="row" spacing={1} alignItems="center">
                          <CustomFileInput
                            id="photo"
                            name="photo"
                            accept="image/*"
                            placeholder="Choose photo..."
                            value={formik.values.photo}
                            onChange={(e) => handleFileChange(e)}
                            error={
                              formik.touched.photo &&
                              Boolean(formik.errors.photo)
                            }
                          />
                          {fileLoading && <CircularProgress size={20} />}
                        </Stack>

                        <FormHelperText variant="error">
                          {formik.touched.photo && formik.errors.photo}
                        </FormHelperText>
                      </FormControl>
                    </Grid>

                    {/* beginning of third row inputs */}

                    <Grid item xs={12} md={4}>
                      <FormControl
                        error={
                          formik.touched.classroom && formik.errors.classroom
                        }
                        fullWidth
                      >
                        <Typography
                          htmlFor="classroom"
                          variant="formSubHeading"
                        >
                          classroom
                        </Typography>
                        <Autocomplete
                          options={classrooms}
                          getOptionLabel={(option) => option.name || ""}
                          isOptionEqualToValue={(option, value) => option.id === value.id}
                          value={classrooms.find(c => c.id === formik.values.classroom) || null}
                          onChange={(_, value) => formik.setFieldValue("classroom", value?.id || "")}
                          onInputChange={(_, value, reason) => { if (reason === "input") setClassroomSearch(value); }}
                          filterOptions={(x) => x}
                          renderInput={(params) => (
                            <StyledTextField
                              {...params}
                              placeholder="Search classroom..."
                              error={formik.touched.classroom && Boolean(formik.errors.classroom)}
                            />
                          )}
                        />
                        <FormHelperText variant="error">
                          {formik.touched.classroom && formik.errors.classroom}
                        </FormHelperText>
                      </FormControl>
                    </Grid>

                    <Grid item xs={12} md={8}>
                      <FormControl error fullWidth>
                        <Typography variant="formSubHeading">address</Typography>
                        <StyledTextField
                          variant="outlined"
                          id="address"
                          name="address"
                          value={formik.values.address}
                          onChange={formik.handleChange}
                          error={
                            formik.touched.address &&
                            Boolean(formik.errors.address)
                          }
                        />
                        <FormHelperText variant="error">
                          {formik.touched.address && formik.errors.address}
                        </FormHelperText>
                      </FormControl>
                    </Grid>
                    {/* end of third row inputs */}

                    <Grid item xs={12} md={6}>
                      <FormControl fullWidth>
                        <Typography variant="formSubHeading">
                          Parent 1 Email (e.g. Mother)
                        </Typography>
                        <StyledTextField
                          variant="outlined"
                          id="parent_email"
                          name="parent_email"
                          placeholder="e.g. mother@email.com"
                          value={formik.values.parent_email}
                          onChange={formik.handleChange}
                        />
                        <FormHelperText>
                          Links this student to Parent 1 for parent portal access.
                        </FormHelperText>
                      </FormControl>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <FormControl fullWidth>
                        <Typography variant="formSubHeading">
                          Parent 2 Email (e.g. Father)
                        </Typography>
                        <StyledTextField
                          variant="outlined"
                          id="parent_email2"
                          name="parent_email2"
                          placeholder="e.g. father@email.com"
                          value={formik.values.parent_email2}
                          onChange={formik.handleChange}
                        />
                        <FormHelperText>
                          Links this student to Parent 2 for parent portal access.
                        </FormHelperText>
                      </FormControl>
                    </Grid>

                    <Grid item xs={12} md={12} textAlign={"right"}>
                      <StyledLoadingButton
                        loading={updateStudentLoading}
                        type="submit"
                      >
                        SUBMIT
                      </StyledLoadingButton>
                    </Grid>
                  </Grid>
                </Box>
              </Box>
            </StyledPaper>
          </Grid>
          <Grid item xs={12} lg={4}>
            <StyledPaper>
              <Box
                className="paperBody"
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  textAlign: "center",
                  "& img": {
                    width: "124px",
                    height: "124px",
                    borderRadius: "50%",
                    border: "5px solid #eeeee",
                    marginBottom: 2,
                  },
                  "& .nameText": {
                    fontSize: "18px",
                    lineHeight: "20px",
                    marginBottom: "5px",
                    fontWeight: 300,
                  },
                  "& .regNo": {
                    marginBottom: "10px",
                  },
                }}
              >
                <UserAvatar
                  src={selectedStudent?.photo}
                  name={`${selectedStudent?.first_name || ""} ${selectedStudent?.last_name || ""}`.trim()}
                  sx={{ width: 124, height: 124, fontSize: "2.5rem", mb: 2, border: "5px solid #eeeeee" }}
                />
                <Typography variant="formHeading" className="nameText">
                  {selectedStudent?.first_name} {selectedStudent?.last_name}
                </Typography>
                <Typography variant="subtitle" className="regNo">
                  Reg No: {selectedStudent?.reg_number}
                </Typography>
              </Box>
            </StyledPaper>
          </Grid>
        </Grid>
      </DialogContent>
    </Dialog>
  );
}

export default EditStudentDialog;
